// domTranslateGuard.ts — evita que o React derrube a tela inteira quando o
// tradutor automático do navegador (Chrome "Traduzir página", Firefox, etc.)
// mexe no DOM em paralelo a um re-render.
//
// Causa raiz (issue conhecida e antiga do próprio React, facebook/react#11538):
// o tradutor envolve nós de texto em elementos <font> e troca o nó original
// por um novo, "desconectando" o nó que o React ainda acha que está lá. Na
// próxima vez que o React tenta remover/inserir esse nó (removeChild/
// insertBefore), o DOM recusa com DOMException — mensagens típicas:
// "Failed to execute 'removeChild' on 'Node': The node to be removed is not
// a child of this node" (Chrome) ou "The object can not be found here."
// (Firefox). O React não tem fallback pra isso e deixa o erro propagar,
// derrubando a árvore inteira no ErrorBoundary mais próximo.
//
// Casos reais na BlinkMed (09-10/09/2026): admin aprovando/editando médico
// (Dra. Débora, Pedro Lola) via /admin — a ação em si sempre completava no
// backend, só a tela quebrava depois num re-render seguinte.
//
// Mitigação usada pela comunidade (mesma técnica do dev.to de julho/2026
// sobre esse bug): faz removeChild/insertBefore tolerantes a um nó que já
// não é mais filho do pai esperado — em vez de lançar, cai num fallback
// inofensivo. Preserva o comportamento normal em 100% dos casos em que o
// DOM não foi mexido por fora do React.

let installed = false;

export function installDomTranslateGuard(): void {
  if (installed) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Node.prototype as any).removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // O tradutor já moveu/substituiu esse nó por fora do React — o resultado
      // que o React queria (o nó sumir do lugar onde ele achava que estava)
      // já é verdade, então não há nada de errado a corrigir aqui.
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Node.prototype as any).insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // O nó de referência foi movido pelo tradutor — insere no final em vez
      // de lançar. Visualmente pode ficar um pixel fora de ordem numa fração
      // de segundo até o próximo re-render corrigir; infinitamente melhor que
      // a tela inteira quebrar.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
